import { Procedure } from '../define-procedure';
import { Store, Actions, ofActionDispatched, ofActionCompleted } from '@ngxs/store';
import { MessageEnum } from '../models/message.enum';
import { RPCEvent } from '../define-event';
import { TakeChapterPicture, TakeChapterPictureSuccess, SetSelectedChapterBackground, SetChapters, SetAllChaptersBackground } from 'src/app/story/state/chapters/story.chapters.actions';
import { StoryChaptersState } from 'src/app/story/state/chapters/story.chapters.state';
import { flatMap, take, catchError, tap, takeUntil, filter } from 'rxjs/operators';
import { of } from 'rxjs/internal/observable/of';
import { BackgroundModel } from 'src/app/engine/models/background.model';
import { EngineService } from 'src/app/engine/engine.service';
import { HumanInterface } from '../models/human.interface';
import { ColorUtils } from 'src/app/shared/libraries/colors/colors';
import { ViewActions } from 'src/app/view/state/view.actions';
import { ViewState } from 'src/app/view/state/view.state';
import { WidgetWiring } from 'src/app/shared/widget-wiring';
import { UiIO } from '../models/ui.io';
import { isEmpty } from 'lodash';
import { SetScrollMode } from 'src/app/engine/state/engine.actions';
import { SetWidgetApiConfig } from 'src/app/widget-auth/state/widget.actions';
import { ViewWindow } from 'src/app/view/models/view-window.enum';
import { ExportImageUtils } from 'src/app/shared/libraries/image/export-image';
import { ImageTypeEnum } from 'src/app/shared/models/image-type.enum';
import { QuizActions } from 'src/app/quiz/state/quiz.actions';
import { HumanReadySDK } from 'src/app/story/state/story.actions';
import { QuizService } from 'src/app/view/components/quiz/view-quiz.service';
import { BehaviorSubject } from 'rxjs';

function toAPIColor(input: BackgroundModel): UiIO.UIBackgroundParam {
  const { colors, gradientType, colorStops } = input;
  return {
    // normalize: [] -> [[]]
    colors: [
      [colors[0], colors[1], colors[2], colors[3]],
      [colors[4], colors[5], colors[6], colors[7]]
    ],
    // as-is
    gradientType,
    colorStops
  };
}

function normalizeColorInput(input: any): [number, number, number, number][] {
  const paramColorValues = ColorUtils.getColors(input)
    .map((parsedColor) => parsedColor['255']);

  return paramColorValues;
}

// #TODO[AC]: should be deprecated in the future and handled by the client's code
function openImageInTab(imageData: string) {
  // iframe needed to bypass data url restriction in window.open
  const imageWindow = window.open('', '_blank');
  if (imageWindow) {
    const img = imageWindow.document.createElement('img');
    img.src = imageData;
    imageWindow.document.body.appendChild(img);
  }
}

function getCurrentBackround(store: Store, params: UiIO.UIBackgroundParam) {
  // get current background
  const currentBackground = store.selectSnapshot(StoryChaptersState.getSelectedChapterBackground);
  const { colors, colorStops, gradientType } = currentBackground;
  // get optional user configured values, else current
  const targetColors = params.colors ? [].concat(...params.colors) : colors;
  const targetGradientType = params.gradientType || gradientType;
  const targetColorStops = params.colorStops || colorStops;
  return new BackgroundModel(targetColors, targetGradientType, targetColorStops);
}

export function getUIEvents(): RPCEvent<UiIO.UIEventResponse>[] {
  return [

  ];
}

export function getUIProcedures(
  Human: HumanInterface,
  store: Store,
  actions: Actions,
  engineService: EngineService,
  quizService: QuizService
): Procedure<UiIO.UIProcRequest, UiIO.UIProcResponse>[] {

  const chaptersSet = new BehaviorSubject(false);

  actions.pipe(
    ofActionCompleted(SetChapters),
    take(1)
  ).subscribe(() => {
    chaptersSet.next(true);
  });

  return [
    // gets the current chapter's background color
    {
      procName: MessageEnum.UIGetBackground,
      actionFn: () => {
        const background = store.selectSnapshot(StoryChaptersState.getSelectedChapterBackground);
        return toAPIColor(background);
      }
    },
    // sets the current chapter's background color
    {
      procName: MessageEnum.UISetBackground,
      // normalize color values
      mapFnIn: (params: UiIO.UIRawBackgroundParam) => {
        const { colors, colorStops, gradientType } = params;
        return <UiIO.UIBackgroundParam>{
          // normalize color input
          colors: colors ? normalizeColorInput(colors) : colors,
          gradientType,
          colorStops
        };
      },
      actionFn: (params: UiIO.UIBackgroundParam) => {
        if (params) {
          // set app background
          const background = getCurrentBackround(store, params);
          store.dispatch(new SetSelectedChapterBackground(background));
        }
        return true;
      }
    },
    // sets the current chapter's background color
    {
      procName: MessageEnum.UIPresetBackground,
      // normalize color values
      mapFnIn: (params: UiIO.UIRawBackgroundParam) => {
        const { colors, colorStops, gradientType } = params;
        return <UiIO.UIBackgroundParam>{
          // normalize color input
          colors: colors ? normalizeColorInput(colors) : colors,
          gradientType,
          colorStops
        };
      },
      actionFn: (params: UiIO.UIBackgroundParam) => {
        if (params) {
          // set app background
          const background = getCurrentBackround(store, params);
          // chapters set: patch all backgrounds
          chaptersSet.pipe(
            filter((chaptersSet) => !!chaptersSet),
            take(1)
          ).subscribe(() => {
            store.dispatch(new SetAllChaptersBackground(background));
          });
        }
        return true;
      }
    },
    // gets the x, y, z world postion of the target point
    {
      procName: MessageEnum.UIGetWorldPosition,
      actionFn: (params: UiIO.UIWorldPositionRequest) => {
        const { worldPos } = Human.renderer.pick.pick(params.x, params.y, { rayPick: true });
        if (worldPos) {
          const { 0: x, 1: y, 2: z } = worldPos;
          return <UiIO.UIWordPositionResponse>{ x, y, z };
        } else {
          return null;
        }
      }
    },
    {
      procName: MessageEnum.UISetOndemand,
      actionFn: (params: UiIO.UISetOndemandExperienceRequest) => {
        store.dispatch([
          new ViewActions.ToggleWindow(ViewWindow.OnDemand, params.enabled),
        ]);
        return true;
      }
    },
    {
      procName: MessageEnum.UISetLoadingExperience,
      actionFn: (interactType: UiIO.UISetLoadingExperienceRequest) => {
        if (!isEmpty(interactType)) {
          store.dispatch([
            new ViewActions.SetInteractType(interactType),
            new ViewActions.OpenWindow(ViewWindow.Interact)
          ]);
        } else {
          store.dispatch(new ViewActions.CloseWindow(ViewWindow.Interact));
        }
        return true;
      }
    },
    {
      procName: MessageEnum.UISetScrollCaptureMode,
      actionFn: (scrollCaptureMode: UiIO.UISetScrollCaptureModeRequest) => {
        store.dispatch(new SetScrollMode(scrollCaptureMode));
        return true;
      }
    },
    {
      procName: MessageEnum.UISetTutorial,
      actionFn: (params: UiIO.UISetTutorialRequest) => {
        store.dispatch([
          new ViewActions.ToggleWindow(ViewWindow.Tutorial, params.enabled),
          new ViewActions.StartTutorial(params.enabled)
        ]);
        return true;
      }
    },
    // gets the display config
    {
      procName: MessageEnum.UIGetDisplay,
      actionFn: () => {
        const layout = store.selectSnapshot(ViewState.getUI);
        const legacyDisplayInfo: UiIO.UIDisplayParamsExternal = {
          audio: layout.audio.enable,
          fullscreen: layout.fullscreen.enable,
          help: layout.help.enable,

          // these all map from Camera
          nav: layout.camera.enable,
          cameraMode: layout.camera.enable,
          center: layout.camera.enable,
          zoomIn: layout.camera.enable,
          zoomOut: layout.camera.enable,

          info: layout.column.enable || layout.drawer.enable,

          // these all map from Media
          playPause: layout.media.enable,
          scrubber: layout.media.enable,
          'scrubber.chapters': layout.media.enable,
          
          tools: layout.tools.enable,
          'tools-display': layout.tools.enable,
          tour: layout.pagination.enable,
          'tour.chapters': layout.pagination.enable,
          objectTree: layout.search.enable,
          anatomyLabels: layout.anatomyLabels.enable,

          // legacy
          share: false,
          dissect: false,
          xray: false,
          annotations: false,
          crossSection: false,
          isolate: false
        };
        return legacyDisplayInfo;
      }
    },
    // sets the display config
    {
      procName: MessageEnum.UISetDisplay,
      actionFn: (params: UiIO.UIDisplayParams) => {
        const apiConfig = WidgetWiring.convertAPIUIConfigToUIConfig(params);
        store.dispatch(new SetWidgetApiConfig(apiConfig));
        return params;
      }
    },
    // resets the UI to the initial layout
    {
      procName: MessageEnum.UIResetDisplay,
      actionFn: () => {
        const initialLayout = store.selectSnapshot(ViewState.getInitialUI);
        store.dispatch(new ViewActions.SetUI(initialLayout));
      }
    },
    // set quiz lock mode
    {
      procName: MessageEnum.UISetQuizLock,
      actionFn: (params: UiIO.UISetQuizLockRequest) => {
        actions.pipe(
          ofActionDispatched(HumanReadySDK),
          take(1)
          ).subscribe(() => {
            store.dispatch(new QuizActions.SetQuizLocked(params));
            if (params === true) {
              quizService.startQuiz();
            }
        });
        return true;
      }
    },
    // take a snapshot of the current chapter
    {
      procName: MessageEnum.UISnapshot,
      aliases: [MessageEnum.SceneSnapshot],
      // normalize params: extend defaults
      mapFnIn: (params: UiIO.UISnapshotParams) => {
        const { width, height } = engineService.getCanvas();
        return {
          width: width,
          height: height,
          openInTab: false,
          fileType: 'jpeg',
          quality: 0.92,
          ...params
        };
      },
      actionFn: (params: UiIO.UISnapshotParams) => {
        const { width, height, openInTab, fileType } = params;
        const currentChapterId = store.selectSnapshot(StoryChaptersState.getSelectedChapterId);
        const dimensions = { width, height };
        const imageType = fileType === 'jpeg' ? ImageTypeEnum.JPEG : ImageTypeEnum.PNG;
        
        return store.dispatch(new TakeChapterPicture(currentChapterId, imageType, 0.92, dimensions, 'original', true, true))
          .pipe(
            // extract success
            flatMap(() => {
              return actions.pipe(
                ofActionDispatched(TakeChapterPictureSuccess),
                take(1)
              );
            }),
            // serialize
            flatMap((e: TakeChapterPictureSuccess) => ExportImageUtils.blobToBase64(e.blob)),
            // open image tab: optional
            tap((imageData: string) => {
              if (openInTab && imageData) {
                openImageInTab(imageData);
              }
            }),
            // handle errors: empty response
            catchError(() => of(null))
          );
      }
    }
  ];
}
